import dayjs from "dayjs";
import type { QueryClient } from "react-query";

import { API_URL } from "../config";
import { getStoredAuthSnapshot } from "../utils";

export type OpsQueryMode = "fast" | "full";

const OPS_SNAPSHOT_STORAGE_PREFIX = "ops-overview-snapshot";
const OPS_SNAPSHOT_MAX_AGE_MS = 2 * 60 * 1000;
const OPS_SNAPSHOT_MAX_ENTRIES = 10;
const OPS_SNAPSHOT_SOFT_SIZE_LIMIT_BYTES = 1 * 1024 * 1024;
const DEFAULT_OPS_MODE: OpsQueryMode = "fast";

type StoredOpsOverviewSnapshot = {
  data: OpsResponse;
  persistedAt: number;
  generatedAt?: string;
};

const normalizeOpsParams = (params: string) => {
  const normalized = `${params || ""}`.trim();
  return normalized.startsWith("?") ? normalized : `?${normalized}`;
};

const buildOpsSearchParams = (
  params: string,
  mode: OpsQueryMode = DEFAULT_OPS_MODE,
  force = false,
) => {
  const searchParams = new URLSearchParams(normalizeOpsParams(params).slice(1));
  searchParams.set("mode", mode);
  if (force) {
    searchParams.set("force", "1");
  }
  return searchParams;
};

const parseOperatorId = (value: unknown) => {
  const parsed = Number.parseInt(`${value ?? ""}`, 10);
  return Number.isFinite(parsed) ? parsed : null;
};

const resolveOperatorId = (value?: unknown) => {
  const direct = parseOperatorId(value);
  if (direct !== null) {
    return direct;
  }

  const authSnapshot = getStoredAuthSnapshot();
  return parseOperatorId(authSnapshot.userData?.operator_ref);
};

const buildOpsSnapshotStorageKey = ({
  operatorId,
  params,
  mode = DEFAULT_OPS_MODE,
}: {
  operatorId: number | null;
  params: string;
  mode?: OpsQueryMode;
}) => {
  const searchParams = buildOpsSearchParams(params, mode);
  return `${OPS_SNAPSHOT_STORAGE_PREFIX}:${operatorId ?? "anonymous"}:${searchParams.toString()}`;
};

const readStoredSnapshot = (storageKey: string): StoredOpsOverviewSnapshot | null => {
  if (typeof window === "undefined" || !window.localStorage) {
    return null;
  }

  const raw = window.localStorage.getItem(storageKey);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<StoredOpsOverviewSnapshot> & {
      storedAt?: string;
    };
    if (!parsed?.data) {
      return null;
    }

    if (typeof parsed.persistedAt === "number" && Number.isFinite(parsed.persistedAt)) {
      return {
        data: parsed.data,
        persistedAt: parsed.persistedAt,
        generatedAt: parsed.generatedAt,
      };
    }

    window.localStorage.removeItem(storageKey);
    return null;
  } catch (_error) {
    try {
      window.localStorage.removeItem(storageKey);
    } catch (_removeError) {
      // Ignore; best-effort cleanup.
    }
    return null;
  }
};

const listSnapshotKeys = (): string[] => {
  if (typeof window === "undefined" || !window.localStorage) {
    return [];
  }

  const keys: string[] = [];
  for (let index = 0; index < window.localStorage.length; index += 1) {
    const key = window.localStorage.key(index);
    if (key && key.startsWith(`${OPS_SNAPSHOT_STORAGE_PREFIX}:`)) {
      keys.push(key);
    }
  }
  return keys;
};

const getSnapshotPersistedAt = (storageKey: string): number => {
  if (typeof window === "undefined" || !window.localStorage) {
    return 0;
  }
  const raw = window.localStorage.getItem(storageKey);
  if (!raw) {
    return 0;
  }
  try {
    const parsed = JSON.parse(raw) as { persistedAt?: number };
    return typeof parsed.persistedAt === "number" && Number.isFinite(parsed.persistedAt)
      ? parsed.persistedAt
      : 0;
  } catch (_error) {
    return 0;
  }
};

const evictOldSnapshots = ({
  excludeKey,
  maxEntries,
}: {
  excludeKey: string;
  maxEntries: number;
}) => {
  if (typeof window === "undefined" || !window.localStorage) {
    return;
  }

  const keys = listSnapshotKeys();
  if (keys.length <= maxEntries) {
    return;
  }

  const sorted = keys
    .map((key) => ({ key, persistedAt: getSnapshotPersistedAt(key) }))
    .sort((a, b) => a.persistedAt - b.persistedAt);

  const toRemove = sorted
    .filter(({ key }) => key !== excludeKey)
    .slice(0, keys.length - maxEntries);
  toRemove.forEach(({ key }) => {
    try {
      window.localStorage.removeItem(key);
    } catch (_error) {
      // Ignore; best-effort cleanup.
    }
  });
};

const clearAllSnapshotsExcept = (excludeKey: string) => {
  if (typeof window === "undefined" || !window.localStorage) {
    return;
  }
  listSnapshotKeys().forEach((key) => {
    if (key === excludeKey) {
      return;
    }
    try {
      window.localStorage.removeItem(key);
    } catch (_error) {
      // Ignore; best-effort cleanup.
    }
  });
};

export const clearStoredOpsOverviewSnapshots = () => {
  if (typeof window === "undefined" || !window.localStorage) {
    return;
  }
  listSnapshotKeys().forEach((key) => {
    try {
      window.localStorage.removeItem(key);
    } catch (_error) {
      // Best-effort cache invalidation after an operations mutation.
    }
  });
};

const buildOverviewSafeSnapshot = (data: OpsResponse): OpsResponse => {
  const trimmedFlights = Array.isArray(data.flights)
    ? data.flights.map((flight) => {
        const {
          ppsVersionHistory: _ppsVersionHistory,
          changeHistory: _changeHistory,
          ...rest
        } = flight as typeof flight & {
          ppsVersionHistory?: unknown;
          changeHistory?: unknown;
        };
        return rest;
      })
    : data.flights;

  return {
    ...data,
    flights: trimmedFlights as OpsResponse["flights"],
  };
};

const isQuotaExceededError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") {
    return false;
  }
  const name = (error as { name?: string }).name;
  const code = (error as { code?: number }).code;
  return (
    name === "QuotaExceededError" ||
    name === "NS_ERROR_DOM_QUOTA_REACHED" ||
    code === 22 ||
    code === 1014
  );
};

export const getOpsQueryKey = ({
  operatorId,
  params,
  mode = DEFAULT_OPS_MODE,
}: {
  operatorId: number | null;
  params: string;
  mode?: OpsQueryMode;
}) => ["ops", operatorId ?? "anonymous", buildOpsSearchParams(params, mode).toString()];

export const fetchOpsOverview = async ({
  params,
  mode = DEFAULT_OPS_MODE,
  force = false,
}: {
  params: string;
  mode?: OpsQueryMode;
  force?: boolean;
}): Promise<OpsResponse> => {
  const searchParams = buildOpsSearchParams(params, mode, force);
  const res = await fetch(`${API_URL}/ops?${searchParams.toString()}`, {
    credentials: "include",
  });

  if (!res.ok) {
    throw new Error(`${res.status}`);
  }

  return res.json();
};

export const getStoredOpsOverviewSnapshot = ({
  operatorId,
  params,
  mode = DEFAULT_OPS_MODE,
}: {
  operatorId?: number | null;
  params: string;
  mode?: OpsQueryMode;
}) => {
  const resolvedOperatorId = resolveOperatorId(operatorId);
  const snapshot = readStoredSnapshot(
    buildOpsSnapshotStorageKey({
      operatorId: resolvedOperatorId,
      params,
      mode,
    }),
  );

  if (!snapshot) {
    return null;
  }

  if (Date.now() - snapshot.persistedAt > OPS_SNAPSHOT_MAX_AGE_MS) {
    return null;
  }

  return snapshot;
};

export const storeOpsOverviewSnapshot = ({
  operatorId,
  params,
  mode = DEFAULT_OPS_MODE,
  data,
}: {
  operatorId?: number | null;
  params: string;
  mode?: OpsQueryMode;
  data: OpsResponse;
}) => {
  if (typeof window === "undefined" || !window.localStorage) {
    return;
  }

  const resolvedOperatorId = resolveOperatorId(operatorId);
  const storageKey = buildOpsSnapshotStorageKey({
    operatorId: resolvedOperatorId,
    params,
    mode,
  });

  const writeSnapshot = (payload: StoredOpsOverviewSnapshot) => {
    const serialized = JSON.stringify(payload);
    window.localStorage.setItem(storageKey, serialized);
    return serialized.length;
  };

  const buildPayload = (snapshotData: OpsResponse): StoredOpsOverviewSnapshot => ({
    data: snapshotData,
    persistedAt: Date.now(),
    generatedAt: snapshotData.generatedAt,
  });

  let payload = buildPayload(data);
  let serializedLength = 0;
  try {
    serializedLength = JSON.stringify(payload).length;
  } catch (_error) {
    return;
  }

  if (serializedLength > OPS_SNAPSHOT_SOFT_SIZE_LIMIT_BYTES) {
    payload = buildPayload(buildOverviewSafeSnapshot(data));
  }

  const attemptWrite = () => {
    try {
      writeSnapshot(payload);
      return true;
    } catch (error) {
      if (!isQuotaExceededError(error)) {
        return false;
      }

      clearAllSnapshotsExcept(storageKey);

      try {
        writeSnapshot(payload);
        return true;
      } catch (retryError) {
        if (!isQuotaExceededError(retryError)) {
          return false;
        }

        payload = buildPayload(buildOverviewSafeSnapshot(data));
        try {
          writeSnapshot(payload);
          return true;
        } catch (_finalError) {
          return false;
        }
      }
    }
  };

  if (!attemptWrite()) {
    return;
  }

  evictOldSnapshots({
    excludeKey: storageKey,
    maxEntries: OPS_SNAPSHOT_MAX_ENTRIES,
  });
};

export const prefetchOpsOverview = async ({
  queryClient,
  params,
  operatorId,
  mode = DEFAULT_OPS_MODE,
}: {
  queryClient: QueryClient;
  params: string;
  operatorId?: number | null;
  mode?: OpsQueryMode;
}) => {
  const resolvedOperatorId = resolveOperatorId(operatorId);

  try {
    const data = await queryClient.fetchQuery(
      getOpsQueryKey({ operatorId: resolvedOperatorId, params, mode }),
      () => fetchOpsOverview({ params, mode }),
      {
        staleTime: OPS_SNAPSHOT_MAX_AGE_MS,
      },
    );

    storeOpsOverviewSnapshot({
      operatorId: resolvedOperatorId,
      params,
      mode,
      data,
    });
  } catch (_error) {
    // Best-effort prefetch only.
  }
};

export const prefetchOpsChunk = () => {
  void import(/* webpackPrefetch: true */ "../pages/Ops").catch(() => {
    // Best-effort: if the dynamic import cannot be scheduled, fall back silently.
  });
};

export const prefetchDefaultOpsOverview = async ({
  queryClient,
  operatorId,
}: {
  queryClient: QueryClient;
  operatorId?: number | null;
}) => {
  prefetchOpsChunk();
  return prefetchOpsOverview({
    queryClient,
    operatorId,
    params: `?date=${dayjs().format("YYYY-MM-DD")}&view=daily`,
  });
};
